import { useState, useEffect, useCallback } from "react";
import { crearAxios } from "@/lib/http";
import { API as API_BASE } from "@/lib/api";

// La URL del backend la resuelve `@/lib/api` (y, debajo, `@/lib/backends`).
// Antes se leía aquí la variable de entorno a pelo: si llegaba vacía, todas
// las peticiones del equipo iban a `/api/equipo` del propio dominio de la web.
const API = `${API_BASE}/equipo`;
const ax = () => crearAxios();

function buildSlug(nombre) {
  return nombre.toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s-]/g, "").trim()
    .replace(/\s+/g, "-");
}

// Backend returns _id; AdminEquipo.js uses .id — normalize here
function normalize(m) {
  return { ...m, id: m._id || m.id };
}

export const useTeam = ({ soloVisibles = false, adminMode = false } = {}) => {
  const [miembros, setMiembros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = adminMode ? `${API}/all` : API;
      const r = await ax().get(endpoint);
      setMiembros((r.data || []).map(normalize));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [adminMode]);

  useEffect(() => { load(); }, [load]);

  const miembrosPublicos = miembros
    .filter((m) => (soloVisibles ? m.visible : true))
    .sort((a, b) => (a.orden ?? 99) - (b.orden ?? 99));

  const addMiembro = useCallback(async (nuevo) => {
    const doc = {
      orden: miembros.length + 1,
      visible: true,
      redes: { instagram: "", twitter: "", youtube: "", tiktok: "" },
      ...nuevo,
      slug: nuevo.slug || buildSlug(nuevo.nombre),
    };
    const r = await ax().post(API, doc);
    const m = normalize(r.data);
    setMiembros(prev => [...prev, m]);
    return m;
  }, [miembros]);

  const updateMiembro = useCallback(async (id, cambios) => {
    const r = await ax().put(`${API}/${id}`, cambios);
    const updated = normalize(r.data);
    setMiembros(prev => prev.map(m => m.id === id ? updated : m));
  }, []);

  const deleteMiembro = useCallback(async (id) => {
    await ax().delete(`${API}/${id}`);
    setMiembros(prev => prev.filter(m => m.id !== id));
  }, []);

  const toggleVisibilidad = useCallback(async (id) => {
    const m = miembros.find(m => m.id === id);
    if (m) await updateMiembro(id, { visible: !m.visible });
  }, [miembros, updateMiembro]);

  const reordenar = useCallback(async (ordenados) => {
    const updated = ordenados.map((m, i) => ({ ...m, orden: i + 1 }));
    setMiembros(updated);
    await ax().patch(`${API}/reordenar`, updated.map(m => m._id));
  }, []);

  const getMiembroBySlug = useCallback((slug) =>
    miembros.find((m) => m.slug === slug) || null, [miembros]);

  const resetToDefault = useCallback(() => load(), [load]);

  return {
    miembros: adminMode ? miembros : miembrosPublicos,
    loading,
    error,
    addMiembro,
    updateMiembro,
    deleteMiembro,
    toggleVisibilidad,
    reordenar,
    getMiembroBySlug,
    resetToDefault,
  };
};
